import { useEffect, useRef, useState } from 'react';

// Debounced autosave hook. Cycles through
//   idle → saving → saved → idle  (or → error)
// based on a host-supplied save function. The host owns the value +
// the persistence side effect; this hook owns the timing.
//
// Behaviour:
//   · 800ms default debounce — fast enough that "Saving…" feels
//     responsive, long enough that mid-word keystrokes don't fire
//     wasted requests.
//   · Skips the very first effect run so loading an existing value
//     doesn't immediately trigger a save.
//   · While a save is in flight, fresh keystrokes mark the work as
//     stale: when the in-flight save resolves it does NOT transition
//     to "Saved" if a newer pending edit exists. Prevents flashing
//     "Saved" while the user is still typing.
//   · Unmount-safe: if the component disappears mid-save, the resolved
//     state update is suppressed (no React warning).
//   · Errors are sticky — they don't auto-fade; the user keeps the
//     signal that their work isn't actually persisted.

const DEFAULT_DEBOUNCE_MS = 800;
const SAVED_FADE_MS = 1500;

export default function useAutosave(value, saveFn, options = {}) {
  const { debounceMs = DEFAULT_DEBOUNCE_MS, enabled = true } = options;
  const [status, setStatus] = useState('idle');
  const debounceRef = useRef(null);
  const savedFadeRef = useRef(null);
  const pendingRef = useRef(0);
  const isInitialMount = useRef(true);
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (savedFadeRef.current) clearTimeout(savedFadeRef.current);
    pendingRef.current += 1;
    const myPending = pendingRef.current;
    // Keep the saving spinner visible across keystrokes if a save is
    // already in flight; otherwise drop quietly back to idle while the
    // debounce window runs.
    setStatus(prev => (prev === 'saving' ? prev : 'idle'));

    debounceRef.current = setTimeout(async () => {
      if (!aliveRef.current) return;
      setStatus('saving');
      try {
        await saveFn(value);
        if (!aliveRef.current) return;
        // Mid-save typing: defer the "Saved" transition to whichever
        // save lands last so we don't lie about freshness.
        if (pendingRef.current !== myPending) return;
        setStatus('saved');
        savedFadeRef.current = setTimeout(() => {
          if (aliveRef.current) setStatus('idle');
        }, SAVED_FADE_MS);
      } catch (err) {
        if (aliveRef.current) setStatus('error');
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, saveFn, enabled, debounceMs]);

  return status;
}
